import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import {
  BaseExceptions,
  getMessageFromErrorResponse,
  isBaseError,
} from '@/api/v4/base-fetch';
import { queryClient, QueryKey } from '@/config/query-client';
import { notify } from '@/ui/snackbar/notify';
import { companyIdState } from '../company.state';
import { useCompanyFetchMode } from '../company-fetch-context';
import type { CompanyFreeTextFieldInput } from '@/api/v4/organization.api';
import { createCompanyFreeTextField } from '@/api/v4/organization.api';
import { useAtomValue } from 'jotai';

export const useCreateCompanyFreeTextField = () => {
  const { t } = useTranslation('default');
  const companyId = useAtomValue(companyIdState);
  const fetchMode = useCompanyFetchMode();

  const mutation = useMutation({
    mutationFn: createCompanyFreeTextField,
    onError: (error: Error) => {
      if (
        isBaseError(error) &&
        error.response.status === BaseExceptions.BAD_REQUEST
      ) {
        notify({
          message: getMessageFromErrorResponse(error) ?? t`unknownError`,
        });
        return;
      }
      notify({
        message: t`unknownError`,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Company, fetchMode, companyId],
        }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.LastActivities] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.LastActivitiesCount],
        }),
      ]);
    },
  });

  const createFreeTextField = (data: CompanyFreeTextFieldInput) =>
    companyId
      ? mutation.mutateAsync({
          organizationId: companyId,
          data,
        })
      : undefined;

  return {
    createFreeTextField,
    isError: mutation.isError,
  };
};
